import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import StudentGame from '@/game/GameComponents/PhaserGame/StudentGames/StudentGame';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { SlottedObject } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/SlottedMinigameCardLayoutGroup';
import {
  vectorDistance,
  waitForSeconds,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import CardDropSite from '@/game/prefabs/StudentGame/CardDropSite';
import { CardObject } from '@/game/prefabs/StudentGame/CardDropSite.types';
import MinigameCardSlot from '@/game/prefabs/StudentGame/MinigameCardSlot';
import SequencedCardDropSite from '@/game/prefabs/StudentGame/SequencedCardDropSite';
import SlottedCardDropSite from '@/game/prefabs/StudentGame/SlottedCardDropSite';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import PlaceholderMinigameCard from '@/game/scenes/PlaceholderStudentGameCard';
import _ from 'lodash';

export class StudentGameDragNDropInputHandler {
  constructor(private studentGame: StudentGame) {
    this.setupDropZones();
  }

  hoveringDropZone: Phaser.GameObjects.Zone | null = null;
  currentlyDraggingCard?: StudentGameCard;
  currentlyDisplacedCard?: {
    card: StudentGameCard;
    dropSite: CardDropSite;
    index: number;
  };
  dropZoneCheckInterval?: NodeJS.Timeout;
  cardDropCooldown: number = 0.15; // 150ms cooldown after dropping a card
  currentlyHoveredCard?: StudentGameCard;

  setupDropZones() {
    const { studentGame } = this;

    // Drag and drop interactivities.
    const defaultDragEnterBehaviour = (
      _pointer: any,
      _gameObject: any,
      dropZone: any,
    ) => {
      this.hoveringDropZone = dropZone;
    };

    const defaultDragOverBehaviour = (
      _pointer: any,
      gameObject: any,
      target: any,
    ) => {
      if (gameObject instanceof StudentGameCard) {
        this.handleCardDragOverZone(gameObject, target);
      }
    };

    const defaultDragLeaveBehaviour = (
      _pointer: any,
      _gameObject: any,
      dropZone: any,
    ) => {
      const { studentGame } = this;
      this.hoveringDropZone = null;
      this.returnDisplacedCard();

      const leftSite = studentGame.getDropSiteFromZone(dropZone);
      if (!leftSite) return;
      const card = this.currentlyDraggingCard;
      if (!card) return;
      if (!card.origin) return;
      // Show the candidate placement back at the card's origin.
      if (leftSite === card.origin.dropSite) {
        leftSite.displayCandidatePlacement(card.origin?.index, card);
      } else {
        card.origin.dropSite.displayCandidatePlacement(card.origin.index, card);
        leftSite.removeCandidatePlacement();
      }
      studentGame.clearAnticipation();
    };

    studentGame.getHandDropSite().init({
      onDragEnter: defaultDragEnterBehaviour,
      onDragOver: defaultDragOverBehaviour,
      onDragLeave: defaultDragLeaveBehaviour,
      onDrop: (_pointer: any, gameObject: any, dropZone: any) => {
        if (this.hoveringDropZone === dropZone) {
          this.handleZoneCardDrop(gameObject, dropZone);
        }
      },
      maxCards: 12,
      displaySlots: this.studentGame.getShouldDisplayBoardSlots() || false,
      initialAlignment: 'left',
      siteName: 'hand',
      layoutWidth: 700,
    });
    // Set a manual drop zone for the hand, rather than the default one.
    if (studentGame instanceof Minigame) {
      studentGame
        .getHandDropSite()
        .overrideDropZone(studentGame.handDropZoneOverride);
    }

    studentGame.getBoardDropSite().init({
      onDragEnter: defaultDragEnterBehaviour,
      onDragOver: defaultDragOverBehaviour,
      onDragLeave: defaultDragLeaveBehaviour,
      onDrop: (_pointer: any, gameObject: any, dropZone: any) => {
        if (this.hoveringDropZone === dropZone) {
          this.handleZoneCardDrop(gameObject, dropZone);
        }
      },
      maxCards: 1,
      displaySlots: studentGame.getShouldDisplayBoardSlots(),
      initialAlignment: 'left',
      siteName: 'board',
    });
    if (studentGame instanceof Minigame) {
      studentGame
        .getBoardDropSite()
        .overrideDropZone(studentGame.boardDropZoneOverride);
    }
  }

  handleZoneCardDrop(card: StudentGameCard, zone: Phaser.GameObjects.Zone) {
    const { studentGame } = this;
    studentGame.getScene().tweens.killTweensOf(card);
    card.setScale(card.targetScale);

    const targetIndex = this.getTargetIndexFromDraggedCard(card, zone);
    const dropSite = studentGame.getDropSiteFromZone(zone);
    if (!dropSite) {
      console.error('No drop site found for card', card);
      return;
    }
    const cardAtTarget = dropSite.layoutGroup?.cards[targetIndex];
    if (cardAtTarget instanceof StudentGameCard) {
      if (card.origin?.dropSite) {
        dropSite.transferCard(
          cardAtTarget,
          card.origin?.dropSite,
          card.origin?.index,
        );
      }
    }
    dropSite.dropCard(card, targetIndex);

    studentGame.setSubmissionPossible(studentGame.getCanSubmit() || false);
    EventBus.emit(GAME_EVENTS.MINIGAME_CARD_DROPPED, {
      card,
      dropSiteName: dropSite.siteName,
      targetIndex,
    });
    studentGame.getHandDropSite().removeCandidatePlacement();
    studentGame.getBoardDropSite().removeCandidatePlacement();
  }
  // Returns the closest index in the drop site's layout group to where the card is currently being dragged.
  // Different drop sites have different ways of calculating this.
  getTargetIndexFromDraggedCard(
    card: StudentGameCard | PlaceholderMinigameCard,
    zone: Phaser.GameObjects.Zone,
  ): number {
    const { studentGame } = this;
    const dropSite = studentGame.getDropSiteFromZone(zone);
    if (dropSite instanceof SlottedCardDropSite) {
      return this.getTargetIndexFromDraggedCardOverSlottedDropSite(
        card,
        dropSite,
      );
    } else if (dropSite instanceof SequencedCardDropSite) {
      return this.getTargetIndexFromDraggedCardOverSequencedDropSite(
        card,
        dropSite,
      );
    }
    return 0;
  }

  getTargetIndexFromDraggedCardOverSlottedDropSite(
    card: StudentGameCard | PlaceholderMinigameCard,
    dropSite: SlottedCardDropSite,
  ): number {
    // Get the position of the card we are dragging.
    const draggingCardPos = { x: card.x, y: card.y };

    // Figure out the index that we are trying to place the card at.
    let closestSlot: Phaser.GameObjects.GameObject | null = null;
    if (dropSite.DropSiteCardContainer?.list.length === 0) {
      closestSlot = null;
    } else {
      closestSlot = dropSite.layoutGroup!.cards.reduce(
        (acc: SlottedObject, curr: SlottedObject) => {
          const cardElement = curr as
            | StudentGameCard
            | MinigameCardSlot
            | PlaceholderMinigameCard;
          const oldElement = acc as
            | StudentGameCard
            | MinigameCardSlot
            | PlaceholderMinigameCard;

          if (curr === null) return acc;
          if (acc === null) return curr;
          if (curr instanceof StudentGameCard && curr.isLocked) return acc;
          if (acc instanceof StudentGameCard && acc.isLocked) return curr;

          const cardPos = cardElement.getDesiredPositionWorld();
          const oldCardPos = oldElement.getDesiredPositionWorld();

          const currentDistance = vectorDistance(
            [cardPos.x, cardPos.y],
            [draggingCardPos.x, draggingCardPos.y],
          );
          const accumulatorDistance = vectorDistance(
            [oldCardPos.x, oldCardPos.y],
            [draggingCardPos.x, draggingCardPos.y],
          );

          return currentDistance < accumulatorDistance ? curr : acc;
        },
      );
    }

    let targetIndex = 0;
    if (closestSlot) {
      targetIndex = dropSite
        .getLayoutGroup()
        .cards.indexOf(closestSlot as SlottedObject);
    }
    return _.clamp(targetIndex, 0, dropSite.getLayoutGroup().data.maxCards);
  }

  getTargetIndexFromDraggedCardOverSequencedDropSite(
    card: StudentGameCard | PlaceholderMinigameCard,
    dropSite: SequencedCardDropSite,
  ): number {
    // Get the position of the card we are dragging.
    const draggingCardPos = { x: card.x, y: card.y };

    // Figure out the index that we are trying to place the card at.
    let closestSlot: Phaser.GameObjects.GameObject | null = null;
    if (dropSite.DropSiteCardContainer?.list.length === 0) {
      closestSlot = null;
    } else {
      closestSlot = dropSite.layoutGroup!.cards.reduce(
        (acc: SlottedObject, curr: SlottedObject) => {
          const cardElement = curr as StudentGameCard | PlaceholderMinigameCard;
          const oldElement = acc as StudentGameCard | PlaceholderMinigameCard;

          if (curr === null) return acc;
          if (acc === null) return curr;

          const cardPos = cardElement.getDesiredPositionWorld();
          const oldCardPos = oldElement.getDesiredPositionWorld();

          const currentDistance = vectorDistance(
            [cardPos.x, cardPos.y],
            [draggingCardPos.x, draggingCardPos.y],
          );

          const accumulatorDistance = vectorDistance(
            [oldCardPos.x, oldCardPos.y],
            [draggingCardPos.x, draggingCardPos.y],
          );

          return currentDistance < accumulatorDistance ? curr : acc;
        },
      );
    }

    if (closestSlot) {
      return dropSite.layoutGroup!.cards.indexOf(closestSlot as CardObject);
    }
    return 0;
  }

  handleCardDragOverZone(card: StudentGameCard, zone: Phaser.GameObjects.Zone) {
    const { studentGame: minigame } = this;
    const targetIndex = this.getTargetIndexFromDraggedCard(card, zone);

    const dropSite = minigame.getDropSiteFromZone(zone);
    if (!dropSite?.layoutGroup) {
      return;
    }

    if (dropSite instanceof SlottedCardDropSite) {
      this.handleCardDragOverSlottedDropSite(card, dropSite, targetIndex);
    } else if (dropSite instanceof SequencedCardDropSite) {
      this.handleCardDragOverSequencedDropSite(card, dropSite, targetIndex);
    } else {
      return;
    }
  }

  handleCardDragOverSlottedDropSite(
    card: StudentGameCard,
    dropSite: SlottedCardDropSite,
    targetIndex: number,
  ) {
    if (!dropSite.layoutGroup) {
      return;
    }
    const { studentGame } = this;
    const target = dropSite.layoutGroup.cards[targetIndex];
    const fromDropSite = card.origin?.dropSite;
    if (
      fromDropSite &&
      dropSite === studentGame.getBoardDropSite() &&
      fromDropSite !== dropSite
    ) {
      if (target instanceof StudentGameCard && target) {
        dropSite.removeCandidatePlacement();
        target.setAnticipation(true);
      } else if (target instanceof MinigameCardSlot) {
        studentGame.updateCandidatePlacement(dropSite, card, targetIndex);
      }
    }

    // Clear anticipation for all but the target card, if it exists.
    const targetAsMinigameCard = target as StudentGameCard;
    studentGame.clearAnticipation(targetAsMinigameCard ?? undefined);

    // Case for dragging from one slot to another.
    if (fromDropSite && fromDropSite === dropSite) {
      // Swap the cards.
      dropSite.layoutGroup.moveCard(
        targetAsMinigameCard,
        card.origin?.index || 0,
      );
      // Update the target card's origin.
      if (target instanceof StudentGameCard && target.origin) {
        target.origin.index = card.origin?.index || 0;
      }
      // Update the dragged card's origin.
      card.origin!.index = targetIndex;

      studentGame.updateCandidatePlacement(dropSite, card, targetIndex);
    }
  }

  handleCardDragOverSequencedDropSite(
    card: StudentGameCard,
    dropSite: SequencedCardDropSite,
    targetIndex: number,
  ) {
    if (!dropSite.layoutGroup) {
      return;
    }
    const { studentGame: minigame } = this;
    minigame.updateCandidatePlacement(dropSite, card, targetIndex);
  }

  async handleCardDrop(
    card: StudentGameCard,
    _pointer: any,
    _dragX: number,
    _dragY: number,
  ) {
    const { studentGame } = this;

    this.currentlyDraggingCard = undefined;
    this.currentlyDisplacedCard = undefined;
    clearInterval(this.dropZoneCheckInterval);
    if (this.hoveringDropZone) {
      // The drop zone will handle this.
      this.hoveringDropZone = null;
    } else {
      if (!card.origin) return;
      card.origin.dropSite.removeCandidatePlacement();
      card.defaultDragEndBehaviour();
    }
    // // Set a small timeout to ensure the card is in the right place
    // // before the user can interact again.
    studentGame.clearAnticipation();
    await waitForSeconds(studentGame.time, this.cardDropCooldown);
    studentGame.setAllDropSitesVisible(false);
  }

  handleStartedCardDragging(pointer: any, card: StudentGameCard) {
    const { studentGame: minigame } = this;
    this.currentlyDraggingCard = card;
    minigame.setAllDropSitesVisible(true);
    const dropSite = minigame.getDropSiteFromCard(card);
    if (!dropSite) return;
    if (!dropSite.layoutGroup) return;

    card.x = pointer.position.x;
    card.y = pointer.position.y;

    PhaserAudioManager.playSFX(minigame.getScene(), 'card-select');

    // Detach the card from its site and record where it came from.
    const index = dropSite.layoutGroup.cards.indexOf(card);
    card.origin = {
      dropSite,
      index,
      desiredPosition: undefined,
    };
    minigame.getScene().tweens.killTweensOf(card);
    const cardScaleFactor = card.getWorldTransformMatrix().scaleX / card.scaleX;
    dropSite.removeCard(card);
    card.x = pointer.position.x;
    card.y = pointer.position.y;
    const cardUpscale = 1.25;
    card.setScale(card.targetScale * cardScaleFactor * cardUpscale);

    // Run this once.
    this.handleCardDragOverZone(card, dropSite.zone!);

    // Placeholder card.
    dropSite.displayCandidatePlacement(index, card);

    EventBus.emit(GAME_EVENTS.MINIGAME_CARD_STARTED_DRAGGING, card);
  }

  handleCardDragged(
    card: StudentGameCard,
    pointer: any,
    _dragX: number,
    _dragY: number,
  ) {
    card.scene.tweens.add({
      targets: card,
      ease: 'Cubic.easeOut',
      x: pointer.position.x,
      y: pointer.position.y,
      duration: 50,
    });
  }

  returnDisplacedCard() {
    if (!this.currentlyDisplacedCard) return;
    this.currentlyDisplacedCard.dropSite.transferCard(
      this.currentlyDisplacedCard.card,
      this.currentlyDisplacedCard.dropSite,
      this.currentlyDisplacedCard.index,
    );
    this.currentlyDisplacedCard = undefined;
  }
}
